import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

/**
 * Page de repli pour https://refoundapp.ca/invite/:token
 *
 * Sur iOS/Android avec l'app installée, le lien ouvre directement l'app
 * (Universal Links / App Links) et cette page ne s'affiche pas.
 * Sinon, elle tente d'ouvrir l'app via le schéme puis propose l'installation.
 *
 * Vérification des liens :
 *   /.well-known/apple-app-site-association  (iOS)
 *   /.well-known/assetlinks.json             (Android)
 */
const APP_STORE_URL = "https://apps.apple.com/app/refound/id6756928133";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.vibecode.smartreceiptmanager&hl=fr-CA";

const Invite = () => {
  const { token } = useParams<{ token: string }>();
  const schemeUrl = token
    ? `smartreceiptmanager://invite/${token}`
    : "smartreceiptmanager://";

  // Tentative d'ouverture automatique de l'app au chargement.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      window.location.href = schemeUrl;
    }, 300);
    return () => window.clearTimeout(timer);
  }, [schemeUrl]);

  const openApp = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = schemeUrl;
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-5 py-10">
      {/* Lueurs d'ambiance */}
      <div className="pointer-events-none absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-64 w-64 rounded-full bg-accent/10 blur-[120px]" />

      <div className="relative z-10 w-full max-w-sm animate-fade-up">
        <div className="glass-card rounded-[28px] p-8 text-center shadow-[0_24px_60px_-20px_hsl(220_20%_2%/0.8)]">
          {/* Icône de l'application */}
          <div className="mx-auto mb-6 inline-flex">
            <img
              src="/icon.png"
              alt="ReFound"
              className="h-20 w-20 rounded-[22px] shadow-[0_10px_30px_-8px_hsl(160_72%_42%/0.4)] ring-1 ring-white/10"
            />
          </div>

          <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            Reçu partagé
          </div>

          <h1 className="font-heading mb-3 text-[26px] font-bold leading-tight text-foreground">
            On vous a partagé un reçu
          </h1>
          <p className="mb-7 text-[15px] leading-relaxed text-muted-foreground">
            Ouvrez ReFound pour consulter le reçu enrichi, les détails de l'achat
            et le mode retour.
          </p>

          <a
            href={schemeUrl}
            onClick={openApp}
            className="group mb-3 flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-4 font-semibold text-primary-foreground shadow-[0_8px_24px_-8px_hsl(160_72%_42%/0.6)] transition-all duration-300 hover:brightness-110 active:scale-[0.98]"
          >
            Ouvrir dans ReFound
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <a
              href={APP_STORE_URL}
              className="flex items-center justify-center rounded-2xl border border-border bg-secondary/60 px-5 py-3.5 text-sm font-medium text-foreground no-underline transition-colors duration-300 hover:bg-secondary"
            >
              Télécharger sur l'App Store
            </a>
            <a
              href={PLAY_STORE_URL}
              className="flex items-center justify-center rounded-2xl border border-border bg-secondary/60 px-5 py-3.5 text-sm font-medium text-foreground no-underline transition-colors duration-300 hover:bg-secondary"
            >
              Disponible sur Google Play
            </a>
          </div>

          <div className="mt-7 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary/80" />
            Lien sécurisé · ReFound
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invite;
